/**
 * Public parsing-rules page (/parsing-rules).
 *
 * A customer-facing explanation of how uploaded values become numbers AND
 * dates: each {@link PARSING_RULES} / {@link DATE_PARSING_RULES} entry as a
 * titled paragraph, then a table of worked examples ({@link PARSING_EXAMPLES} /
 * {@link DATE_PARSING_EXAMPLES}). Standalone like the style guide (no app
 * shell, no auth) so it can be linked to a customer directly.
 *
 * The rule + example copy comes from the shared, already-plain-English data
 * (`@repo/shared/common`), kept in lockstep with the parsers by the anti-drift
 * tests (`numeric-parse.test.ts` / `date-parse.test.ts`); the page's own chrome
 * copy goes through i18n.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  APP_NAME,
  DATE_PARSING_EXAMPLES,
  DATE_PARSING_RULES,
  PARSING_EXAMPLES,
  PARSING_RULES,
} from '@repo/shared/common';
import type { DateGrain } from '@repo/shared/common';
import {
  Body,
  Caption,
  Display,
  Heading,
  Stack,
  Table,
} from '~/components/ui';
import styles from './parsing-rules.module.css';

/**
 * Render a parsed `YYYYMMDD` ordinal as the calendar value a customer reads:
 * day grain → `2024-01-15`, month grain → `2024-01` (the day is only a pin).
 */
function formatDateOrdinal(ordinal: number, grain: DateGrain | null): string {
  const year = Math.floor(ordinal / 10000);
  const month = String(Math.floor(ordinal / 100) % 100).padStart(2, '0');
  const day = String(ordinal % 100).padStart(2, '0');
  return grain === 'month' ? `${year}-${month}` : `${year}-${month}-${day}`;
}

export function ParsingRulesPage(): ReactNode {
  const { t } = useTranslation();

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.brand}>
          <span className={styles.brandMark}>{APP_NAME.charAt(0)}</span>
          <span className={styles.brandWord}>{APP_NAME}</span>
        </div>
      </header>

      <main className={styles.content}>
        <Stack gap="lg">
          <Stack gap="sm">
            <Display>{t('parsingRules.title')}</Display>
            <Body>{t('parsingRules.intro')}</Body>
          </Stack>

          <Stack gap={0}>
            {PARSING_RULES.map((rule) => (
              <section key={rule.id} className={styles.rule ?? ''}>
                <Heading level={3} className={styles.ruleTitle ?? ''}>
                  {rule.title}
                </Heading>
                <Body>{rule.detail}</Body>
              </section>
            ))}
          </Stack>

          <Stack gap="sm">
            <Heading level={2}>{t('parsingRules.examplesTitle')}</Heading>
            <Body>{t('parsingRules.examplesIntro')}</Body>

            <Table.ScrollContainer minWidth={480}>
              <Table verticalSpacing="sm" striped aria-label={t('parsingRules.examplesTitle')}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('parsingRules.columns.input')}</Table.Th>
                    <Table.Th>{t('parsingRules.columns.result')}</Table.Th>
                    <Table.Th>{t('parsingRules.columns.note')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {PARSING_EXAMPLES.map((example) => (
                    <Table.Tr key={`${example.input}-${example.note}`}>
                      <Table.Td>
                        <span className={styles.inputCell ?? ''}>{example.input}</span>
                      </Table.Td>
                      <Table.Td>
                        {example.parsed === null ? (
                          <Caption className={styles.resultText ?? ''}>
                            {t('parsingRules.keptAsText')}
                          </Caption>
                        ) : (
                          <span className={styles.resultNumber ?? ''}>{String(example.parsed)}</span>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Body size="sm">{example.note}</Body>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>

          <Stack gap="sm">
            <Heading level={2}>{t('parsingRules.dates.title')}</Heading>
            <Body>{t('parsingRules.dates.intro')}</Body>
          </Stack>

          <Stack gap={0}>
            {DATE_PARSING_RULES.map((rule) => (
              <section key={rule.id} className={styles.rule ?? ''}>
                <Heading level={3} className={styles.ruleTitle ?? ''}>
                  {rule.title}
                </Heading>
                <Body>{rule.detail}</Body>
              </section>
            ))}
          </Stack>

          <Stack gap="sm">
            <Heading level={2}>{t('parsingRules.dates.examplesTitle')}</Heading>
            <Body>{t('parsingRules.dates.examplesIntro')}</Body>

            <Table.ScrollContainer minWidth={560}>
              <Table
                verticalSpacing="sm"
                striped
                aria-label={t('parsingRules.dates.examplesTitle')}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('parsingRules.columns.input')}</Table.Th>
                    <Table.Th>{t('parsingRules.dates.columns.format')}</Table.Th>
                    <Table.Th>{t('parsingRules.columns.result')}</Table.Th>
                    <Table.Th>{t('parsingRules.columns.note')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {DATE_PARSING_EXAMPLES.map((example) => (
                    <Table.Tr key={`${example.input}-${example.format}`}>
                      <Table.Td>
                        <span className={styles.inputCell ?? ''}>{example.input}</span>
                      </Table.Td>
                      <Table.Td>
                        <Body size="sm">{t(`parsingRules.dates.formats.${example.format}`)}</Body>
                      </Table.Td>
                      <Table.Td>
                        {example.ordinal === null ? (
                          <Caption className={styles.resultText ?? ''}>
                            {t('parsingRules.keptAsText')}
                          </Caption>
                        ) : (
                          <span className={styles.resultNumber ?? ''}>
                            {formatDateOrdinal(example.ordinal, example.grain)}
                          </span>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Body size="sm">{example.note}</Body>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        </Stack>
      </main>
    </div>
  );
}
